import { useCallback, useEffect, useMemo, useState } from "react";
import { addDays, addMonths, format } from "date-fns";
import { CalendarClock, ListChecks, Plane, Users } from "lucide-react";
import { api } from "@/lib/api";
import { rangeFor, daysInRange, ymd, hoursOf } from "@/lib/dates";
import { can } from "@/lib/colors";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { CalendarToolbar } from "@/components/calendar/CalendarToolbar";
import { PersonalView } from "@/components/calendar/PersonalView";
import { TeamGrid } from "@/components/calendar/TeamGrid";
import { MonthView } from "@/components/calendar/MonthView";
import { Legend } from "@/components/calendar/Chips";
import { EventDialog } from "@/components/calendar/EventDialog";
import { Skeleton } from "@/components/ui/skeleton";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl bg-white border border-slate-200 px-4 py-3 flex items-center gap-3" data-testid={testid}>
    <span className="w-9 h-9 rounded-xl bg-[#EAF2F2] text-[#24474A] grid place-items-center"><Icon className="w-4 h-4" /></span>
    <div className="leading-tight">
      <div className="text-xl font-bold text-slate-800">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">{label}</div>
    </div>
  </div>
);

const rangeLabel = (view, from, to, anchor) => {
  if (view === "day") return format(anchor, "EEEE, d MMM yyyy");
  if (view === "month") return format(anchor, "MMMM yyyy");
  return `${format(from, "d MMM")} – ${format(to, "d MMM yyyy")}`;
};

export default function Dashboard() {
  const { user } = useAuth();
  const lead = can(user, "view_team");
  const [adding, setAdding] = useState(null);
  const addTask = (date, assignee) => {
    if (assignee && assignee !== user.id && !can(user, "assign_tasks")) return;
    setAdding({ date, assignee: assignee || user.id });
  };
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  const [dept, setDept] = useState("all");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [from, to] = rangeFor(view, anchor);
  const start = ymd(from);
  const end = ymd(to);

  const load = useCallback(async () => {
    const params = { start, end };
    if (dept !== "all") params.department = dept;
    const { data } = await api.get("/calendar", { params });
    setData(data);
  }, [start, end, dept]);

  useEffect(() => {
    load();
  }, [load]);

  const move = (dir) => {
    if (view === "day") setAnchor((a) => addDays(a, dir));
    else if (view === "week") setAnchor((a) => addDays(a, 7 * dir));
    else setAnchor((a) => addMonths(a, dir));
  };

  const names = useMemo(() => Object.fromEntries((data?.users || []).map((u) => [u.id, u.name])), [data]);
  const days = daysInRange(from, to);
  const hours = data ? data.shifts.reduce((n, s) => n + hoursOf(s), 0) : 0;

  return (
    <div>
      <PageHeader
        title={lead ? "Team calendar" : `Hello, ${user.name.split(" ")[0]}`}
        subtitle={lead ? "Every team member's shifts, tasks and leave side by side. Click any day to add a task." : "Your shifts, tasks and who's off. Click any day to add a task."}
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6 fade-up" style={{ animationDelay: "60ms" }}>
        <Stat icon={CalendarClock} label="Shifts in view" value={data ? data.shifts.length : "–"} testid="stat-shifts" />
        <Stat icon={Users} label="Scheduled hours" value={data ? Math.round(hours) : "–"} testid="stat-hours" />
        <Stat icon={ListChecks} label="Open tasks" value={data ? data.tasks.filter((t) => t.status !== "done").length : "–"} testid="stat-open-tasks" />
        <Stat icon={Plane} label="Leave bookings" value={data ? data.holidays.length : "–"} testid="stat-leave" />
      </div>
      <div className="rounded-3xl bg-white/60 border border-slate-200/80 p-3 sm:p-5 fade-up" style={{ animationDelay: "120ms" }}>
        <CalendarToolbar
          label={rangeLabel(view, from, to, anchor)} view={view} setView={setView}
          onPrev={() => move(-1)} onNext={() => move(1)} onToday={() => setAnchor(new Date())}
          dept={dept} setDept={setDept} showDept={lead}
        />
        {!data ? (
          <Skeleton className="h-[420px] rounded-2xl" />
        ) : view === "month" ? (
          <MonthView days={days} data={data} monthIndex={anchor.getMonth()} team={lead} onSelect={setSelected} onAdd={addTask} onOpenDay={(d) => { setAnchor(new Date(d + "T00:00")); setView("day"); }} />
        ) : lead ? (
          <TeamGrid days={days} data={data} onSelect={setSelected} onAdd={addTask} />
        ) : (
          <PersonalView days={days} data={data} userId={user.id} onSelect={setSelected} onAdd={addTask} />
        )}
        <div className="mt-4"><Legend /></div>
      </div>
      <TaskDialog open={!!adding} presetDate={adding?.date} presetAssignee={adding?.assignee} onClose={() => setAdding(null)} onSaved={load} />
      <EventDialog selected={selected} names={names} onClose={() => setSelected(null)} onChanged={load} />
    </div>
  );
}
